import { createI18n } from 'vue-i18n';
import en from './locales/en';
import cs from './locales/cs';

export const SUPPORTED_LOCALES = ['en', 'cs'] as const;
export type AppLocale = (typeof SUPPORTED_LOCALES)[number];

export const LOCALE_LABELS: Record<AppLocale, { label: string; short: string }> = {
  en: { label: 'English', short: 'EN' },
  cs: { label: 'Čeština', short: 'CZ' },
};

const STORAGE_KEY = 'keyzo_locale';

function isSupported(value: string | null | undefined): value is AppLocale {
  return !!value && (SUPPORTED_LOCALES as readonly string[]).includes(value);
}

/** Resolve the initial locale: saved preference → browser language → English. */
export function resolveInitialLocale(): AppLocale {
  try {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (isSupported(saved)) return saved;
  } catch {
    /* localStorage unavailable (private mode / SSR) — fall through */
  }
  const nav = typeof navigator !== 'undefined' ? navigator.language?.slice(0, 2) : undefined;
  return isSupported(nav) ? nav : 'en';
}

const initialLocale = resolveInitialLocale();

export const i18n = createI18n({
  legacy: false,
  globalInjection: true,
  locale: initialLocale,
  fallbackLocale: 'en',
  messages: { en, cs },
  // CLDR-correct plural form selection. vue-i18n's default rule is not
  // language-aware, so we map the count to the message-form index per locale.
  pluralRules: {
    // English: one (n===1) | other. Messages: "singular | plural".
    en: (choice: number) => (choice === 1 ? 0 : 1),
    // Czech: one (1) | few (2–4) | other (0, 5+). Messages: "one | few | other".
    cs: (choice: number) => {
      const n = Math.abs(choice);
      if (n === 1) return 0;
      if (n >= 2 && n <= 4) return 1;
      return 2;
    },
  },
});

/** Switch the active locale, persist the choice, and update <html lang>. */
export function setLocale(locale: AppLocale): void {
  i18n.global.locale.value = locale;
  try {
    localStorage.setItem(STORAGE_KEY, locale);
  } catch {
    /* ignore persistence failures */
  }
  if (typeof document !== 'undefined') {
    document.documentElement.setAttribute('lang', locale);
  }
}

export function getLocale(): AppLocale {
  return i18n.global.locale.value as AppLocale;
}

// Reflect the initial locale on the document element at startup.
if (typeof document !== 'undefined') {
  document.documentElement.setAttribute('lang', initialLocale);
}
